import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import _ from 'lodash';

import { openModalAction } from '../../../actions/modal';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { getCapChargeText, signalRender, getVitalsData, convertUnixEpochToHumanReadable } from '../../../utils';
import { actions } from '../../Organizations/redux/actions';
import { getUrlForDevice } from './HubDeviceEvents';
import { DeviceTypes } from '../../../utils/deviceTypes';
import { useGlobalPageSize } from '../../../utils/tableUtils';

function HubDevices(props) {
  const hubId = encodeURIComponent(props.match.params.id);
  const globalPageSize = useGlobalPageSize();
  const pageRequest = React.useRef({
    offset: 0,
    limit: globalPageSize,
    search: '',
    filterBy: '',
  });
  const [refreshEnable, setRefreshEnable] = React.useState(true);

  React.useEffect(() => {
    props.loadCaps(hubId, pageRequest.current);

    return () => props.clearData();
  }, []);

  const onSearchQueryChange = (query) => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    props.loadCaps(hubId, pageRequest.current);
  };

  const onTextInputChange = (e) => {
    onSearchQueryChange(e.target.value);
  };

  const onBackButton = () => {
    const url = props.match.url;
    const updateURL = url.replace(`/${hubId}/devices`, '');
    if (props.history.location.state?.params?.offset) {
      props.onNavigate(updateURL, { offset: props.history.location.state.params?.offset });
    } else {
      props.onNavigate(updateURL, { offset: 0 });
    }
  };

  const onRowSelected = device => {
    const url = getUrlForDevice(device, device.device_type);
    props.onNavigate(url, null, `?lastActivity=${device.timestamp}&hubId=${hubId}`);
  };

  const onRefresh = () => {
    setRefreshEnable(false);
    setTimeout(() => {
      setRefreshEnable(true);
    }, 10000);
    props.loadCaps(hubId, pageRequest.current);
  };

  const onOffsetChange = React.useCallback((offset) => {
    pageRequest.current.offset = offset;
    props.loadCaps(hubId, pageRequest.current);
  }, [hubId]);

  const onPageSizeChange = React.useCallback((newSize) => {
    pageRequest.current.offset = 0;
    pageRequest.current.limit = newSize;
    props.loadCaps(hubId, pageRequest.current);
  }, [hubId]);

  const getHeaderComponents = () => {
    return (
      <React.Fragment>
        <TextInput class="search" placeholder={Strings.search} onChange={onTextInputChange} />
        <Button class="refresh" disabled={!refreshEnable} onClick={onRefresh} />
      </React.Fragment>
    );
  };

  const onCapsDelete = (id) => {
    const cap = props.devices[id];
    const deviceId = cap.device_id;
    const data = {
      title: (
        <span>
          {Strings.deleteHubDeviceWarning}{' '}
          <b>{cap.device_id.split('.').length >= 2 ? cap.device_id.split('.')[1] : cap.device_id}</b>?
        </span>
      ),
      onConfirmAction: actions.deleteHubDevice(hubId, deviceId, [actions.getHubDevices(hubId, pageRequest.current)]),
      onCancelAction: null,
    };
    props.openConfirmModal(data);
  };

  const deleteDisabled = (id) => {
    const cap = props.devices[id];
    return (
      cap.device_type !== DeviceTypes['Weighing Machine'].id &&
      cap.device_type !== DeviceTypes['Blood Pressure Monitor'].id &&
      cap.device_type !== DeviceTypes['Bluetooth Cap'].id &&
      cap.device_type !== DeviceTypes['Wrist Band'].id &&
      cap.device_type !== DeviceTypes.Glucometer.id &&
      cap.device_type !== DeviceTypes.Spirometer.id
    );
  };

  const renderDeviceId = (e) => {
    const prefix = e.manufacturer.concat('.', e.model);
    let trueDeviceId = e.device_id.replace(prefix, '');
    if (trueDeviceId.startsWith('.')) trueDeviceId = trueDeviceId.slice(1);
    trueDeviceId = trueDeviceId.replace('rxcap.', '');
    return trueDeviceId;
  };

  const columns = React.useMemo(() => [
    columnHelper.accessor('device_id', {
      header: Strings.deviceId,
      cell: info => renderDeviceId(info.row.original),
      size: 150,
    }),
    columnHelper.accessor('deviceModel', {
      header: Strings.deviceModel,
      cell: info => {
        const e = info.row.original;
        return e.manufacturer.concat(' ', e.model);
      },
      size: 150,
    }),
    columnHelper.accessor('timestamp', {
      header: Strings.lastActivity,
      cell: info => (info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-'),
      size: 180,
    }),
    columnHelper.accessor('device_type', {
      header: Strings.deviceType,
      cell: info => info.getValue() || '-',
      size: 120,
    }),
    columnHelper.accessor('last_reading', {
      header: Strings.lastReading,
      cell: info => (!_.isEmpty(info.getValue()) ? getVitalsData(info.getValue()) : '-'),
      size: 150,
    }),
    columnHelper.accessor('last_pairing_status', {
      header: Strings.pairingStatus,
      cell: info => info.getValue() || '-',
      size: 120,
    }),
    columnHelper.accessor('last_pairing_timestamp', {
      header: Strings.pairingTime,
      cell: info => (info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-'),
      size: 150,
    }),
  ], []);

  const buttons = [
    {
      icon: 'delete',
      onClick: onCapsDelete,
      disabled: deleteDisabled,
      text: Strings.delete,
    },
  ];

  const { devices, isLoading, pagination, capInfo } = props;
  const selectedHub = props.match.params.id;
  const signalStrength = capInfo ? capInfo.signal_strength : undefined;
  const battery = capInfo ? capInfo.battery : undefined;

  return (
    <React.Fragment>
      {!props.noHeader && (
        <div className="infoHeader">
          <div className="rowDirection">
            <button className="back" onClick={() => onBackButton()} tabIndex={0}>
              {Strings.back}
            </button>
            <div className="feildBox">
              {Strings.deviceId}: <div className="feildValue">{selectedHub}</div>
            </div>
          </div>
          <div className={'rowDirection left'}>
            <div className="feildBox">
              {Strings.signal}: <div className="feildValue">{signalRender(signalStrength)}</div>
            </div>
            <div className="pathHr"></div>
            <div className="feildBox">
              {Strings.battery.battery}: <div className="feildValue">{getCapChargeText(battery)}</div>
            </div>
          </div>
        </div>
      )}
      <div className="details-container">
        <PageHeader right={() => getHeaderComponents()} />
        {devices && (
          <Table3
            name="hub-devices"
            uuid="96dffcdd-f1cd-483e-a98a-4216ba7d1601"
            data={devices}
            columns={columns}
            onRowClick={onRowSelected}
            onOffsetChange={onOffsetChange}
            onPageSizeChange={onPageSizeChange}
            pagination={pagination}
            isLoading={isLoading}
            buttons={buttons}
            enableColumnFiltering
          />
        )}
      </div>
    </React.Fragment>
  );
}

HubDevices.propTypes = {
  clearData: PropTypes.func,
  devices: PropTypes.any,
  history: PropTypes.shape({
    location: PropTypes.shape({ state: PropTypes.shape({ params: PropTypes.shape({ offset: PropTypes.any }) }) }),
  }),
  isLoading: PropTypes.any,
  loadCaps: PropTypes.func,
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.any }),
    url: PropTypes.shape({ replace: PropTypes.func }),
  }),
  onNavigate: PropTypes.func,
  openConfirmModal: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
  }),
  capInfo: PropTypes.shape({
    signal_strength: PropTypes.string,
    battery: PropTypes.number,
  }),
  noHeader: PropTypes.bool,
};

HubDevices.defaultProps = { noHeader: false };

const mapStateToProps = state => {
  const { organizations } = state.superUser;
  return {
    role: state.auth.role,
    devices: organizations && organizations?.hubDevices,
    isLoading: organizations && organizations?.isLoading,
    pagination: organizations && organizations?.pagination,
    capInfo: organizations && organizations?.capInfo,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: (path, params, search) =>
    dispatch(
      push({
        pathname: path,
        state: { params },
        search,
      }),
    ),
  loadCaps: (hubId, pageRequest) => dispatch(actions.getHubDevices(hubId, pageRequest)),
  clearData: () => dispatch(actions.clearData()),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HubDevices);
